import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * Módulo especializado para extracción de datos de tablas
 * Maneja múltiples formatos: React Table, HTML, TanStack, etc.
 */
class TableDataExtractor {
    constructor(options = {}) {
        this.options = {
            maxRows: options.maxRows || 1000,
            maxColumns: options.maxColumns || 50,
            enableProgressiveExtraction: options.enableProgressiveExtraction || true,
            fallbackToCapture: options.fallbackToCapture || true,
            ...options
        };

        // Referencia al módulo principal si se necesita
        this.printModule = null;
    }

    /**
     * Establecer referencia al módulo principal
     */
    setPrintModule(printModule) {
        this.printModule = printModule;
    }

    /**
     * ✅ MÉTODO PRINCIPAL: Extraer datos de tabla universal
     */
    async extractTableData_09062025() {
        console.log('📋 Extrayendo datos de tabla con detección automática...');

        try {
            // ✅ MANTENER LÓGICA ORIGINAL DE BÚSQUEDA DE CONTENEDORES
            const tableContainers = [
                this.config.selector,
                '#tabla-estadias',
                '#card-listado-paradas',
                '#tabla-kilometraje',
                '#tabla-bitacoras',        // ✅ AGREGAR BITÁCORAS
                '#tabla-recorridos',
                '#tabla-alertas',
                '[id*="tabla-"]',
                '[id*="table-"]',
                '[data-component="table"]',
                '.table-container',
                '.list-striped',
                'table'
            ];

            let tableContainer = null;
            for (const selector of tableContainers) {
                tableContainer = document.querySelector(selector);
                if (tableContainer) {
                    console.log(`📍 Contenedor encontrado con: ${selector}`);
                    break;
                }
            }

            if (!tableContainer) {
                console.warn('❌ No se encontró contenedor de tabla');
                return null;
            }

            // ✅ MANTENER LÓGICA ORIGINAL DE DETECCIÓN
            const tableType = this.detectTableType(tableContainer);
            console.log(`🔍 Tipo de tabla detectado: ${tableType}`);

            // ✅ PREPARAR TABLA SI ES NECESARIO (MANTENER)
            if (window.prepareTableForPrint) {
                await window.prepareTableForPrint();
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // ✅ MANTENER CASOS EXISTENTES + AGREGAR BITÁCORAS
            switch (tableType) {
                case 'AlertaTableListTanstack':
                    return await this.extractAlertaTableListTanstackFull(tableContainer);

                case 'BitacoraTableListTanstack': // ✅ NUEVO CASO
                    return await this.extractBitacoraTableListTanstack(tableContainer);

                case 'RecorridosTableList':
                    return await this.extractRecorridosTableListFull(tableContainer);

                case 'ParadasNoAutorizadas':
                    return await this.extractParadasNoAutorizadas(tableContainer);

                case 'HTMLTable':
                    return await this.extractHTMLTable(tableContainer);

                default:
                    console.warn(`⚠️ Tipo de tabla no reconocido: ${tableType}, usando extractor genérico`);
                    return await this.extractFromReactTable(tableContainer);
            }

        } catch (error) {
            console.error('❌ Error en extractTableData:', error);
            return null;
        }
    }

    async extractConsumosTableListTanstack(container) {
        console.log('📝 Extrayendo datos de ConsumoTableListTanstack...');
        const tableData = {
            headers: ['Alerta', 'Vehículo', 'Fecha', 'Destinatario', 'Mensaje', 'Consumo'],
            data: [],
            title: 'Listado de consumos',
            totalRows: 0,
            source: 'ConsumoTableListTanstack'
        };

        try {
            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('.card-header h6, [data-table-title="true"]');
            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ EXTRAER FILAS DE DATOS (.alert-row SIN data-cell)
            const dataRows = container.querySelectorAll('.alert-row');
            console.log(`📊 Procesando ${dataRows.length} filas de consumos...`);

            dataRows.forEach((row, index) => {
                // ✅ BUSCAR DIVS DIRECTOS (3 columnas esperadas)
                const cellDivs = row.children; // Hijos directos

                if (cellDivs.length >= 6) {
                    const alerta = cellDivs[0]?.textContent?.trim() || '';
                    const vehiculo = cellDivs[1]?.textContent?.trim() || '';
                    const fecha = cellDivs[2]?.textContent?.trim() || '';
                    const destinatario = cellDivs[3]?.textContent?.trim() || '';
                    const mensaje = cellDivs[4]?.textContent?.trim() || '';
                    const consumo = cellDivs[5]?.textContent?.trim() || '';

                    if (alerta || vehiculo || fecha || destinatario || mensaje || consumo) {
                        tableData.data.push([alerta, vehiculo, fecha, destinatario, mensaje, consumo]);
                    }
                }
            });

            tableData.totalRows = tableData.data.length;

            console.log(`✅ BitacoraTableListTanstack extraída: ${tableData.data.length} filas`);
            console.log('📋 Headers:', tableData.headers);
            console.log('📊 Primeras 3 filas:', tableData.data.slice(0, 3));

            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo BitacoraTableListTanstack:', error);
            return tableData;
        }
    }

    async extractEstadiasTableList(container) {
        console.log('📝 Extrayendo datos de EstadiasTableList...');

        const tableData = {
            headers: [],
            data: [],
            title: 'Listado Estadias',
            totalRows: 0,
            source: 'EstadiasTableList',
            columnWeights: this.options.tableColumnWeights || null
        };

        try {
            const titleElement = container.querySelector('.card-header h6, [data-table-title="true"]');
            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // Forzar headers ordenados para evitar desplazamientos (ignora la columna de expansión vacía)
            tableData.headers = [
                'Chofer',
                'Evento',
                'Marcador',
                'Fecha de inicio',
                'Fecha de fin',
                'Duración',
                'Coordenadas',
                'Ubicación'
            ];

            const rows = container.querySelectorAll('tbody tr');
            const expected = tableData.headers.length;

            rows.forEach(row => {
                const cells = Array.from(row.querySelectorAll('td'));

                // La fila MASTER tiene solo 3 celdas: [expand][no_economico][estadiaStr]
                // La fila DETAIL (child) tiene aprox 'expected+1' celdas (con columna expand)
                const isMasterRow = cells.length <= 3;

                let rowData;

                if (isMasterRow) {
                    // Fila maestra: extraer vehiculo + resumen de estadias
                    // cells[0]=expand, cells[1]=no_economico, cells[2]=estadiaStr
                    const vehiculo = cells[1]?.textContent?.trim() || '';
                    const resumen = cells[2]?.textContent?.trim() || '';

                    rowData = Array(expected).fill('');
                    rowData[0] = vehiculo;   // Columna Chofer: "P-668 / P-668"
                    rowData[1] = resumen;    // Columna Evento: "Estadías: 4, Duración: 03:16:55 hr(s)"

                    // Si no hubo separacion clara, fallback al texto completo en col 0
                    if (!vehiculo && !resumen) {
                        rowData[0] = this.cleanCellText(row.textContent) || 'Resumen';
                    }
                } else {
                    // Fila detalle: alinear celdas con headers (ignorar columna expand inicial)
                    const offset = Math.max(0, cells.length - expected);
                    const alignedCells = cells.slice(offset, offset + expected);
                    const safeCells = alignedCells.length >= expected ? alignedCells.slice(0, expected) : alignedCells;

                    rowData = safeCells.map((cell, idx) => {
                        if (idx === expected - 1) {
                            // Ubicación: tomar texto visible (span de direccion) y no el ícono del mapa
                            const locationSpan = cell.querySelector('span');
                            const text = locationSpan?.textContent?.trim() || cell.textContent?.trim() || '';
                            return text || 'Ubicación no disponible';
                        }

                        if (idx === expected - 2) {
                            // Coordenadas: tomar solo el texto (quitar tooltip/overlay)
                            const coordSpan = cell.querySelector('span');
                            const text = coordSpan?.textContent?.trim() || cell.textContent?.trim() || '';
                            return text || '';
                        }

                        const raw = cell.textContent?.trim() || '';
                        return raw;
                    });
                }

                if (rowData.some(value => value !== '')) {
                    tableData.data.push(rowData);
                }
            });

            tableData.totalRows = tableData.data.length;
            console.log(`✅ EstadiasTableList extraída: ${tableData.data.length} filas`);
            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo EstadiasTableList:', error);
            return tableData;
        }
    }
    async extractBitacoraTableListTanstack(container) {
        console.log('📝 Extrayendo datos de BitacoraTableListTanstack...');

        // ✅ VERIFICAR EMPRESA IGUAL QUE EN TablaBitacoras.js
        const empresaId = localStorage.getItem('empresa_id');
        const shouldHideCategories = empresaId !== '9';

        console.log(`🏢 EmpresaId: ${empresaId}, shouldHideCategories: ${shouldHideCategories}`);

        const tableData = {
            headers: shouldHideCategories
                ? ['Observaciones', 'Nombre Usuario', 'Fecha']
                : ['Observaciones', 'Nombre Usuario', 'Fecha', 'Categoría'],
            data: [],
            title: 'Listado Bitácora',
            totalRows: 0,
            source: 'BitacoraTableListTanstack',
            isBitacoraTable: true // ✅ MARCADOR PARA IDENTIFICAR TABLA DE BITÁCORAS
        };

        try {
            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('.card-header h6, [data-table-title="true"]');
            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ EXTRAER FILAS DE DATOS (.alert-row SIN data-cell)
            const dataRows = container.querySelectorAll('.alert-row');
            console.log(`📊 Procesando ${dataRows.length} filas de bitácoras...`);

            dataRows.forEach((row, index) => {
                // ✅ BUSCAR DIVS DIRECTOS (3 o 4 columnas dependiendo de la empresa)
                const cellDivs = row.children; // Hijos directos
                const minCells = shouldHideCategories ? 3 : 4;

                if (cellDivs.length >= minCells) {
                    const observaciones = cellDivs[0]?.textContent?.trim() || '';
                    const usuario = cellDivs[1]?.textContent?.trim() || '';
                    const fecha = cellDivs[2]?.textContent?.trim() || '';

                    const rowData = [observaciones, usuario, fecha];

                    // ✅ SOLO AGREGAR CATEGORÍA SI NO SE DEBE OCULTAR (empresaId === '9')
                    if (!shouldHideCategories && cellDivs[3]) {
                        const categoria = cellDivs[3]?.textContent?.trim() || '';
                        rowData.push(categoria);
                    }

                    if (observaciones || usuario || fecha) {
                        tableData.data.push(rowData);
                    }
                }
            });

            tableData.totalRows = tableData.data.length;

            console.log(`✅ BitacoraTableListTanstack extraída: ${tableData.data.length} filas`);
            console.log('📋 Headers:', tableData.headers);
            console.log('📊 Primeras 3 filas:', tableData.data.slice(0, 3));

            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo BitacoraTableListTanstack:', error);
            return tableData;
        }
    }


    async extractTableData_respaldo() {
        console.log('📋 Extrayendo datos de tabla con detección automática...');

        try {
            // ✅ 1. BUSCAR CUALQUIER CONTENEDOR DE TABLA AUTOMÁTICAMENTE
            const tableContainers = [
                // Contenedores específicos conocidos
                '#tabla-kilometraje',
                '#tabla-recorridos',
                '#tabla-alertas',
                // Patrones genéricos
                '[id*="tabla-"]',
                '[id*="table-"]',
                '[data-component="table"]',
                '.table-container',
                '.list-striped',
                '[class*="tanstack"]',
                // Fallback: cualquier tabla
                'table'
            ];

            let tableContainer = null;
            for (const selector of tableContainers) {
                tableContainer = document.querySelector(selector);
                if (tableContainer) {
                    console.log(`📍 Contenedor encontrado con: ${selector}`);
                    break;
                }
            }

            if (!tableContainer) {
                console.warn('❌ No se encontró contenedor de tabla');
                return null;
            }

            // ✅ 2. DETECTAR TIPO Y CONFIGURACIÓN AUTOMÁTICAMENTE
            const config = this.detectTableConfig(tableContainer);
            console.log('🔍 Configuración detectada:', config);

            // ✅ 3. PREPARAR TABLA SI ES NECESARIO
            if (window.prepareTableForPrint) {
                await window.prepareTableForPrint();
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // ✅ 4. EXTRAER CON CONFIGURACIÓN AUTO-DETECTADA
            return await this.extractWithConfig(tableContainer, config);

        } catch (error) {
            console.error('❌ Error en extractTableData:', error);
            return null;
        }
    }



    detectTableConfig(container) {
        const config = {
            type: 'html',
            title: 'Datos de Tabla',
            selector: container.id || 'tabla-generica',
            headerSelector: 'thead th, thead td',
            bodySelector: 'tbody tr',
            cellSelector: 'td',
            transforms: {}
        };

        // ✅ DETECTAR TIPO POR CONTENIDO Y ESTRUCTURA
        const tables = container.querySelectorAll('table');
        const hasMultipleTables = tables.length > 1;
        const hasTanstack = container.querySelector('[class*="tanstack"]') ||
            container.querySelector('.alert-row[data-cell]');

        // ✅ DETECTAR TÍTULO AUTOMÁTICAMENTE
        const titleElements = [
            container.querySelector('.card-header h6'),
            container.querySelector('[data-table-title="true"]'),
            container.querySelector('h5, h4, h3'),
            container.querySelector('.mb-0')
        ];

        for (const titleEl of titleElements) {
            if (titleEl && titleEl.textContent.trim()) {
                config.title = titleEl.textContent.trim();
                break;
            }
        }

        // ✅ DETECTAR TIPO POR PATTERNS
        const text = container.textContent || '';

        if (hasMultipleTables) {
            config.type = 'dual-table';
            console.log('🔍 Detectado: Tabla dual (headers separados)');
        } else if (hasTanstack) {
            config.type = 'tanstack';
            config.bodySelector = '.alert-row[data-cell], [role="row"]';
            config.cellSelector = '[data-cell], [role="cell"]';
            console.log('🔍 Detectado: Tabla Tanstack');
        } else if (container.querySelector('table')) {
            config.type = 'html';
            console.log('🔍 Detectado: Tabla HTML estándar');
        }

        // ✅ DETECTAR TRANSFORMACIONES AUTOMÁTICAMENTE
        if (container.querySelector('button .fa-map-marker-alt')) {
            config.transforms['button .fa-map-marker-alt'] = 'Ver Ubicación';
        }
        if (container.querySelector('button[class*="btn-light"]')) {
            config.transforms['button[class*="btn-light"]'] = 'Ver Mapa';
        }

        // ✅ DETECTAR SELECTORES ESPECÍFICOS POR TIPO
        if (text.includes('Kilometraje') || text.includes('Distancia')) {
            config.reportType = 'kilometraje';
        } else if (text.includes('Recorrido') || text.includes('Evento')) {
            config.reportType = 'recorridos';
        } else if (text.includes('Alerta') || text.includes('Alert')) {
            config.reportType = 'alertas';
        }

        return config;
    }

    /**
     * ✅ EXTRAER CON CONFIGURACIÓN AUTO-DETECTADA
     */
    async extractWithConfig(container, config) {
        console.log(`🔍 Extrayendo tabla tipo: ${config.type}`);

        const tableData = {
            headers: [],
            data: [],
            title: config.title,
            totalRows: 0,
            source: config.type,
            reportType: config.reportType
        };

        try {
            switch (config.type) {
                case 'dual-table':
                    return await this.extractDualTable(container, config);

                case 'tanstack':
                    return await this.extractTanstackTable(container, config);

                case 'html':
                default:
                    return await this.extractHtmlTable(container, config);
            }
        } catch (error) {
            console.error(`❌ Error extrayendo tabla ${config.type}:`, error);
            return tableData;
        }
    }

    async extractHtmlTable(container, config) {
        console.log('🔧 Extrayendo tabla HTML genérica...');

        const tableData = {
            headers: [],
            data: [],
            title: config.title,
            totalRows: 0,
            source: 'html-auto'
        };

        try {
            const table = container.querySelector('table');
            if (!table) {
                console.warn('⚠️ No se encontró tabla HTML');
                return tableData;
            }

            // ✅ EXTRAER HEADERS
            // NO filtrar headers vacios: deben mantener el mismo indice que las celdas
            // para preservar la alineacion header[i] <-> cell[i] en el PDF.
            // Las columnas de expand (header: ' ') se renderizan como columna vacia pero
            // conservan su posicion.
            const headerRows = table.querySelectorAll(config.headerSelector);
            if (headerRows.length > 0) {
                tableData.headers = Array.from(headerRows).map(cell =>
                    cell.textContent?.trim() || ''
                );
            }

            // ✅ EXTRAER FILAS
            const dataRows = table.querySelectorAll(config.bodySelector);
            dataRows.forEach(row => {
                const cells = row.querySelectorAll(config.cellSelector);
                if (cells.length > 0) {
                    const rowData = Array.from(cells).map(cell => {
                        let content = cell.textContent?.trim() || '';

                        // ✅ APLICAR TRANSFORMACIONES
                        for (const [selector, replacement] of Object.entries(config.transforms)) {
                            if (cell.querySelector(selector)) {
                                content = replacement;
                                break;
                            }
                        }

                        return content;
                    }).filter(content => content !== '');

                    if (rowData.length > 0) {
                        tableData.data.push(rowData);
                    }
                }
            });

            tableData.totalRows = tableData.data.length;

            console.log(`✅ Tabla extraída automáticamente: ${tableData.data.length} filas`);
            return tableData;

        } catch (error) {
            console.error('❌ Error en extractHtmlTable:', error);
            return tableData;
        }
    }

    async extractDualTable(container, config) {
        console.log('🔧 Extrayendo tabla dual...');

        const tableData = {
            headers: [],
            data: [],
            title: config.title,
            totalRows: 0,
            source: 'dual-table-auto'
        };

        try {
            const tables = container.querySelectorAll('table');
            let headerTable = null;
            let dataTable = null;

            // ✅ IDENTIFICAR TABLA DE HEADERS Y DATOS
            tables.forEach(table => {
                const bodyRows = table.querySelectorAll('tbody tr');
                const headerCells = table.querySelectorAll('thead th, thead td');

                if (headerCells.length > 0 && bodyRows.length === 0) {
                    headerTable = table;
                } else if (bodyRows.length > 0) {
                    dataTable = table;
                }
            });

            // ✅ EXTRAER HEADERS
            // NO filtrar headers vacios: mantener alineacion header[i] <-> cell[i]
            if (headerTable) {
                const headerCells = headerTable.querySelectorAll('thead th, thead td');
                tableData.headers = Array.from(headerCells).map(cell =>
                    cell.textContent?.trim() || ''
                );
            }

            // ✅ EXTRAER DATOS
            if (dataTable) {
                const dataRows = dataTable.querySelectorAll('tbody tr');
                dataRows.forEach(row => {
                    const cells = row.querySelectorAll('td');
                    if (cells.length > 0) {
                        const rowData = Array.from(cells).map(cell => {
                            let content = cell.textContent?.trim() || '';

                            // ✅ APLICAR TRANSFORMACIONES
                            for (const [selector, replacement] of Object.entries(config.transforms)) {
                                if (cell.querySelector(selector)) {
                                    content = replacement;
                                    break;
                                }
                            }

                            return content;
                        }).filter(content => content !== '');

                        if (rowData.length > 0) {
                            tableData.data.push(rowData);
                        }
                    }
                });
            }

            tableData.totalRows = tableData.data.length;
            console.log(`✅ Tabla dual extraída: ${tableData.data.length} filas`);
            return tableData;

        } catch (error) {
            console.error('❌ Error en extractDualTable:', error);
            return tableData;
        }
    }



    async extractTableData() {
        console.log('📋 Extrayendo datos de tabla con expansión completa...');

        try {
            // ✅ 1. BUSCAR CONTENEDOR DE TABLA (PRIORIZAR RECORRIDOS)
            // const tableContainer = document.querySelector('#tabla-recorridos') ||
            //                      document.querySelector('[data-component="table"]') ||
            //                      document.querySelector('.table-container') ||
            //                      document.querySelector('.list-striped') ||
            //                      document.querySelector('[class*="tanstack"]') ||
            //                     document.querySelector('[id*="tabla-"]') ||
            //                     document.querySelector('[id*="table-"]');
            const tableContainer = document.querySelector('#tabla-bitacoras') ||      // ✅ AGREGAR
                document.querySelector('#tabla-estadias') ||
                document.querySelector('#tabla-consumos') ||
                document.querySelector('#tabla-recorridos') ||
                document.querySelector('#tabla-alertas') ||
                document.querySelector('#card-listado-paradas') ||
                document.querySelector('#tabla-kilometraje') ||
                document.querySelector('[data-component="table"]') ||
                document.querySelector('.table-container') ||
                document.querySelector('.list-striped') ||
                document.querySelector('[class*="tanstack"]') ||
                document.querySelector('[id*="tabla-"]') ||
                document.querySelector('[id*="table-"]');


            if (!tableContainer) {
                console.warn('❌ No se encontró contenedor de tabla');
                return null;
            }

            console.log('📋 Contenedor encontrado:', tableContainer.className || tableContainer.id);

            // ✅ 2. PREPARAR TABLA PARA CAPTURA COMPLETA (ESTO ERA CLAVE)
            if (window.prepareTableForPrint) {
                console.log('🔧 Preparando tabla para captura completa...');
                await window.prepareTableForPrint();

                // ✅ ESPERAR MÁS TIEMPO PARA QUE SE RENDERICEN TODAS LAS FILAS
                await new Promise(resolve => setTimeout(resolve, 2000));
                console.log('✅ Tabla preparada para captura completa');
            } else {
                console.warn('⚠️ prepareTableForPrint() no está disponible');
            }

            // ✅ 3. EXTRAER USANDO DETECCIÓN AUTOMÁTICA MEJORADA
            return await this.extract(tableContainer, 'auto');

        } catch (error) {
            console.error('❌ Error en extractTableData:', error);
            return null;
        } finally {
            // ✅ 4. RESTAURAR TABLA DESPUÉS DE EXTRACCIÓN
            if (window.restoreTableAfterPrint) {
                console.log('🔄 Restaurando tabla después de extracción...');
                setTimeout(() => {
                    window.restoreTableAfterPrint();
                }, 1000);
            }
        }
    }

    async extractTableData_bakbueno_09062025() {
        console.log('📋 Extrayendo datos de tabla con expansión completa...');

        try {
            // ✅ 1. BUSCAR CONTENEDOR DE TABLA (PRIORIZAR RECORRIDOS)
            const tableContainer = document.querySelector('#tabla-recorridos') ||
                document.querySelector('[data-component="table"]') ||
                document.querySelector('.table-container') ||
                document.querySelector('.list-striped') ||
                document.querySelector('[class*="tanstack"]');

            if (!tableContainer) {
                console.warn('❌ No se encontró contenedor de tabla');
                return null;
            }

            console.log('📋 Contenedor encontrado:', tableContainer.className || tableContainer.id);

            // ✅ 2. PREPARAR TABLA PARA CAPTURA COMPLETA (ESTO ERA CLAVE)
            if (window.prepareTableForPrint) {
                console.log('🔧 Preparando tabla para captura completa...');
                await window.prepareTableForPrint();

                // ✅ ESPERAR MÁS TIEMPO PARA QUE SE RENDERICEN TODAS LAS FILAS
                await new Promise(resolve => setTimeout(resolve, 2000));
                console.log('✅ Tabla preparada para captura completa');
            } else {
                console.warn('⚠️ prepareTableForPrint() no está disponible');
            }

            // ✅ 3. EXTRAER USANDO DETECCIÓN AUTOMÁTICA MEJORADA
            return await this.extract(tableContainer, 'auto');

        } catch (error) {
            console.error('❌ Error en extractTableData:', error);
            return null;
        } finally {
            // ✅ 4. RESTAURAR TABLA DESPUÉS DE EXTRACCIÓN
            if (window.restoreTableAfterPrint) {
                console.log('🔄 Restaurando tabla después de extracción...');
                setTimeout(() => {
                    window.restoreTableAfterPrint();
                }, 1000);
            }
        }
    }

    async extractTableData_respaldo2() {
        console.log('📋 Extrayendo datos de tabla con expansión completa...');

        try {
            // ✅ 1. BUSCAR CONTENEDOR DE TABLA
            const tableContainer = document.querySelector('[data-component="table"]') ||
                document.querySelector('.table-container') ||
                document.querySelector('.list-striped') ||
                document.querySelector('[class*="tanstack"]');

            if (!tableContainer) {
                console.warn('❌ No se encontró contenedor de tabla');
                return null;
            }

            console.log('📋 Contenedor encontrado:', tableContainer.className);

            // ✅ 2. PREPARAR TABLA PARA CAPTURA COMPLETA (ESTO ERA CLAVE)
            if (window.prepareTableForPrint) {
                console.log('🔧 Preparando tabla para captura completa...');
                await window.prepareTableForPrint();

                // ✅ ESPERAR MÁS TIEMPO PARA QUE SE RENDERICEN TODAS LAS FILAS
                await new Promise(resolve => setTimeout(resolve, 2000));
                console.log('✅ Tabla preparada para captura completa');
            } else {
                console.warn('⚠️ prepareTableForPrint() no está disponible');
            }

            // ✅ 3. EXTRAER USANDO LA LÓGICA QUE FUNCIONABA
            return await this.extractAlertaTableListTanstackFull(tableContainer);

        } catch (error) {
            console.error('❌ Error en extractTableData:', error);
            return null;
        } finally {
            // ✅ 4. RESTAURAR TABLA DESPUÉS DE EXTRACCIÓN
            if (window.restoreTableAfterPrint) {
                console.log('🔄 Restaurando tabla después de extracción...');
                setTimeout(() => {
                    window.restoreTableAfterPrint();
                }, 1000);
            }
        }
    }

    /**
     * ✅ EXTRACTOR COMPLETO RESTAURADO - BASADO EN LA LÓGICA QUE FUNCIONABA
     */
    async extractAlertaTableListTanstackFull(container) {
        console.log('🎯 Extrayendo datos completos de AlertaTableListTanstack...');

        const tableData = {
            headers: ['Fecha', 'Alerta', 'Vehículo', 'Random', 'Tiempo abierto', 'Chofer', 'Geocerca', 'Ubicación', 'Video'],
            data: [],
            title: 'Listado de Alertas',
            totalRows: 0,
            source: 'AlertaTableListTanstack'
        };

        try {
            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('[data-table-title="true"]') ||
                container.querySelector('.card-header h6') ||
                container.querySelector('.card-title');

            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ EXTRAER HEADERS
            const headerRow = container.querySelector('[data-table-headers="true"]');
            if (headerRow) {
                const headerCells = headerRow.querySelectorAll('[data-column]');
                if (headerCells.length > 0) {
                    tableData.headers = Array.from(headerCells).map(cell =>
                        cell.textContent?.trim() || ''
                    );
                    console.log('📋 Headers extraídos:', tableData.headers);
                }
            }

            // ✅ EXTRAER TODAS LAS FILAS (ahora deberían estar todas renderizadas)
            const dataRows = container.querySelectorAll('.alert-row');
            console.log(`📊 Total de filas .alert-row encontradas: ${dataRows.length}`);

            if (dataRows.length === 0) {
                console.warn('⚠️ No se encontraron filas .alert-row');
                // ✅ FALLBACK: Buscar otras estructuras
                return await this.extractFromAlternativeStructure(container, tableData);
            }

            // ✅ PROCESAR FILAS CON LA LÓGICA QUE FUNCIONABA
            const processedRows = this.processAlertRows(dataRows, tableData.headers);
            tableData.data = processedRows;
            tableData.totalRows = processedRows.length;

            console.log(`✅ Tabla extraída: ${tableData.data.length} filas, ${tableData.headers.length} columnas`);
            console.log('📋 Primeras 3 filas:', tableData.data.slice(0, 3));

            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo tabla AlertaTableListTanstack:', error);
            return tableData;
        }
    }

    processAlertRows(dataRows, headers = []) {
        console.log('🔄 Procesando filas de alertas...');

        const processedRows = [];
        const normalizedHeaders = Array.isArray(headers)
            ? headers.map(header => header?.toLowerCase?.() || '')
            : [];

        const columnExtractors = normalizedHeaders.length > 0
            ? normalizedHeaders.map(headerLabel => {
                if (headerLabel.includes('fecha')) {
                    return (row) => row.querySelector('[data-cell="fecha"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('alerta')) {
                    return (row) => row.querySelector('[data-cell="alerta"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('veh')) {
                    return (row) => row.querySelector('[data-cell="vehiculo"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('random')) {
                    return (row) => row.querySelector('[data-cell="random"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('tiempo') || headerLabel.includes('abierto') || headerLabel.startsWith('t.')) {
                    return (row) => row.querySelector('[data-cell="tiempo_abierto"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('chofer')) {
                    return (row) => row.querySelector('[data-cell="chofer"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('geocerca') || headerLabel.includes('marcador')) {
                    return (row) => row.querySelector('[data-cell="geocerca"]')?.textContent?.trim() || '';
                }
                if (headerLabel.includes('ubic')) {
                    return (row) => {
                        const btn = row.querySelector('[data-cell="ubicacion"] a');
                        return btn ? 'Disponible' : (row.querySelector('[data-cell="ubicacion"]')?.textContent?.trim() || '');
                    };
                }
                if (headerLabel.includes('video')) {
                    return (row) => {
                        const videoCell = row.querySelector('[data-cell="video"]');
                        if (!videoCell) return '';
                        const icon = videoCell.querySelector('svg');
                        if (icon) {
                            return 'Disponible';
                        }
                        return videoCell.textContent?.trim() || '';
                    };
                }
                return () => '';
            })
            : [];

        dataRows.forEach(row => {
            if (columnExtractors.length > 0) {
                const rowData = columnExtractors.map(extractor => extractor(row));
                if (rowData.some(cell => cell && cell.length > 0)) {
                    processedRows.push(rowData);
                    return;
                }
            }

            const rowText = row.textContent?.trim();
            if (rowText && rowText.length > 10) {
                const parts = rowText.split(/\s{2,}|\t/).filter(p => p.trim().length > 0);
                if (parts.length >= 3) {
                    const sliceLength = columnExtractors.length > 0 ? columnExtractors.length : 9;
                    processedRows.push(parts.slice(0, sliceLength));
                }
            }
        });

        console.log(`✅ Filas procesadas: ${processedRows.length}`);
        return processedRows;
    }

    async extractFromAlternativeStructure(container, tableData) {
        console.log('🔄 Intentando extracción desde estructura alternativa...');

        try {
            // ✅ BUSCAR CUALQUIER ESTRUCTURA QUE CONTENGA DATOS
            const potentialRows = container.querySelectorAll('div[class*="row"], tr, .list-item');
            console.log(`📊 Filas potenciales encontradas: ${potentialRows.length}`);

            const extractedRows = [];

            potentialRows.forEach(row => {
                const text = row.textContent?.trim();

                // ✅ BUSCAR PATRONES DE FECHA PARA IDENTIFICAR FILAS DE DATOS
                if (text && text.match(/\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}/)) {
                    // Dividir el texto en columnas posibles
                    const parts = text.split(/\s{2,}|\t|;|,/).filter(p => p.trim().length > 0);

                    if (parts.length >= 3) {
                        extractedRows.push(parts.slice(0, 9)); // Máximo 9 columnas
                    }
                }
            });

            if (extractedRows.length > 0) {
                tableData.data = extractedRows;
                tableData.totalRows = extractedRows.length;
                console.log(`✅ Extracción alternativa exitosa: ${extractedRows.length} filas`);
            }

            return tableData;

        } catch (error) {
            console.error('❌ Error en extracción alternativa:', error);
            return tableData;
        }
    }

    /**
     * ✅ MÉTODO UNIVERSAL MEJORADO
     */
    async extractUniversalTableData() {
        return await this.extractTableData();
    }

    async detectAndExtractTableData() {
        return await this.extractTableData();
    }

    /**
     * ✅ EXTRACCIÓN ESPECÍFICA PARA AlertaTableListTanstack
     */
    async extractAlertaTableListTanstack() {
        return await this.extractTableData();
    }

    /**
     * ✅ EXTRACCIÓN DE TABLAS REACT GENERALES
     */
    async extractReactTableData() {
        return await this.extractTableData();
    }

    async extractHTMLTableData() {
        return await this.extractTableData();
    }

    async extractCustomTableData() {
        return await this.extractTableData();
    }

    async extractAnyTableData() {
        return await this.extractTableData();
    }

    async extractFromDOMStructure() {
        return await this.extractTableData();
    }

    /**
     * ✅ EXTRAER HEADERS DE LA TABLA
     */
    extractHeaders(table) {
        try {
            const headers = [];

            // Estrategia 1: thead > tr > th
            const headerCells = table.querySelectorAll('thead th, thead td');
            if (headerCells.length > 0) {
                headerCells.forEach(cell => {
                    headers.push(this.cleanHeaderText(cell.textContent));
                });
                return headers;
            }

            // Estrategia 2: primera fila como headers
            const firstRow = table.querySelector('tr');
            if (firstRow) {
                const cells = firstRow.querySelectorAll('th, td');
                cells.forEach(cell => {
                    headers.push(this.cleanHeaderText(cell.textContent));
                });
                return headers;
            }

            // Estrategia 3: role-based headers
            const roleHeaders = table.querySelectorAll('[role="columnheader"]');
            if (roleHeaders.length > 0) {
                roleHeaders.forEach(header => {
                    headers.push(this.cleanHeaderText(header.textContent));
                });
                return headers;
            }

            return [];

        } catch (error) {
            console.warn('Error extrayendo headers:', error);
            return [];
        }
    }

    /**
     * ✅ EXTRAER FILAS DE DATOS
     */
    extractTableRows(table) {
        try {
            const rows = [];

            // Estrategia 1: tbody > tr
            const dataRows = table.querySelectorAll('tbody tr');
            if (dataRows.length > 0) {
                dataRows.forEach(row => {
                    const rowData = this.extractRowData(row);
                    if (rowData && rowData.length > 0) {
                        rows.push(rowData);
                    }
                });
                return rows;
            }

            // Estrategia 2: todas las filas excepto la primera (si es header)
            const allRows = table.querySelectorAll('tr');
            const hasExplicitHeaders = table.querySelector('thead');
            const startIndex = hasExplicitHeaders ? 0 : 1;

            for (let i = startIndex; i < allRows.length; i++) {
                const rowData = this.extractRowData(allRows[i]);
                if (rowData && rowData.length > 0) {
                    rows.push(rowData);
                }
            }

            return rows;

        } catch (error) {
            console.warn('Error extrayendo filas:', error);
            return [];
        }
    }

    /**
     * ✅ EXTRAER DATOS DE UNA FILA ESPECÍFICA
     */
    extractRowData(row) {
        try {
            const rowData = [];
            const cells = row.querySelectorAll('td, th');

            cells.forEach(cell => {
                rowData.push(this.cleanCellText(cell.textContent));
            });

            return rowData;

        } catch (error) {
            console.warn('Error extrayendo datos de fila:', error);
            return [];
        }
    }

    /**
     * ✅ EXTRAER TÍTULO DE LA TABLA
     */
    extractTableTitle(container) {
        try {
            // Buscar título en varios lugares
            const titleSelectors = [
                'caption',
                '.table-title',
                'h1, h2, h3, h4, h5, h6',
                '[data-title]',
                '.card-header',
                '.panel-heading'
            ];

            for (const selector of titleSelectors) {
                const titleElement = container.querySelector(selector);
                if (titleElement && titleElement.textContent.trim()) {
                    return this.cleanHeaderText(titleElement.textContent);
                }
            }

            // Buscar en contenedor padre
            const parent = container.closest('.card, .panel, .table-wrapper');
            if (parent) {
                const parentTitle = parent.querySelector('h1, h2, h3, h4, h5, h6');
                if (parentTitle) {
                    return this.cleanHeaderText(parentTitle.textContent);
                }
            }

            return null;

        } catch (error) {
            console.warn('Error extrayendo título:', error);
            return null;
        }
    }

    // /**
    //  * ✅ EXTRACCIÓN DESDE ESTRUCTURA DOM GENERAL
    //  */
    // async extractFromDOMStructure() {
    //     console.log('🔍 Intentando extracción desde estructura DOM general...');

    //     try {
    //         // Buscar patrones comunes de datos tabulares
    //         const dataContainers = document.querySelectorAll(
    //             '.row, .data-row, [class*="row"], .list-item, [class*="item"]'
    //         );

    //         const extractedData = [];
    //         const potentialHeaders = new Set();

    //         for (const container of dataContainers) {
    //             const rowData = this.extractDataFromElement(container);
    //             if (rowData && Object.keys(rowData).length > 0) {
    //                 extractedData.push(rowData);
    //                 Object.keys(rowData).forEach(key => potentialHeaders.add(key));
    //             }
    //         }

    //         if (extractedData.length > 0) {
    //             const headers = Array.from(potentialHeaders);
    //             const data = extractedData.map(row => headers.map(header => row[header] || ''));

    //             return {
    //                 headers: headers,
    //                 data: data,                    // ✅ Formato consistente
    //                 rows: data,                    // ✅ Alias
    //                 title: 'Datos Extraídos del DOM',
    //                 totalRows: data.length,
    //                 source: 'DOMStructure'
    //             };
    //         }

    //         return null;

    //     } catch (error) {
    //         console.warn('Error en extracción DOM:', error);
    //         return null;
    //     }
    // }

    /**
     * ✅ EXTRAER DATOS DE UN ELEMENTO GENERAL
     */
    extractDataFromElement(element) {
        try {
            const data = {};

            // Buscar patrones de label-value
            const labelValuePairs = element.querySelectorAll('[class*="label"], [class*="key"], [class*="field"]');

            labelValuePairs.forEach(labelEl => {
                const label = this.cleanHeaderText(labelEl.textContent);
                const valueEl = labelEl.nextElementSibling || labelEl.querySelector('[class*="value"]');

                if (valueEl) {
                    data[label] = this.cleanCellText(valueEl.textContent);
                }
            });

            return Object.keys(data).length > 0 ? data : null;

        } catch (error) {
            return null;
        }
    }

    /**
     * ✅ MÉTODOS AUXILIARES DE LIMPIEZA
     */
    cleanHeaderText(text) {
        if (!text) return '';
        return text.trim()
            .replace(/\s+/g, ' ')
            .replace(/[^\w\s\-_áéíóúñÁÉÍÓÚÑ]/g, '')
            .trim();
    }

    cleanCellText(text) {
        if (!text) return '';
        return text.trim()
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * ✅ VERIFICAR SI UN ELEMENTO ES VISIBLE
     */
    isElementVisible(element) {
        try {
            if (!element) return false;

            const rect = element.getBoundingClientRect();
            const style = window.getComputedStyle(element);

            return rect.width > 0 &&
                rect.height > 0 &&
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                style.opacity !== '0';

        } catch (error) {
            return false;
        }
    }



    /**
     * ✅ MÉTODO PARA EXTRAER DATOS DE TABLA CON CAPTURA
     */

    async extractRecorridosTableListFull(container) {
        console.log('🚗 Extrayendo datos completos de RecorridosTableList...');

        // ✅ VERIFICAR EMPRESA (solo empresa 153 muestra columnas extras)
        const empresaId = localStorage.getItem('empresa_id');
        const shouldShowExtras = empresaId === '153';

        console.log(`🏢 EmpresaId: ${empresaId}, shouldShowExtras: ${shouldShowExtras}`);

        const baseHeaders = ['Fecha', 'Evento', 'Velocidad(Km/h)', 'Marcador', 'Dirección', 'Ubicación'];
        const optionalHeaders = ['Random', 'Tiempo abierto'];

        const tableData = {
            headers: [...baseHeaders], // se recalcula más abajo según DOM
            data: [],
            title: 'Listado de Recorridos',
            totalRows: 0,
            source: 'RecorridosTableList'
        };

        try {
            // ✅ PREPARAR TABLA Y ESPERAR MÁS TIEMPO
            if (window.prepareRecorridosTableForPrint) {
                console.log('🔧 Preparando RecorridosTableList específicamente...');
                await window.prepareRecorridosTableForPrint();

                // ✅ ESPERAR TIEMPO ADICIONAL PARA RE-RENDER
                console.log('⏳ Esperando re-render de la tabla...');
                await new Promise(resolve => setTimeout(resolve, 3000));

                console.log('✅ RecorridosTableList preparada completamente');
            }

            // ✅ BUSCAR TABLA CON LOGS DETALLADOS
            const tables = container.querySelectorAll('table');
            console.log(`📊 Tablas encontradas: ${tables.length}`);

            if (tables.length === 0) {
                console.warn('⚠️ No se encontraron tablas en RecorridosTableList');
                console.log('🔍 Contenido del container:', container.innerHTML.substring(0, 500));
                return tableData;
            }

            // ✅ REVISAR TODAS LAS TABLAS PARA ENCONTRAR LA CORRECTA
            let tableConDatos = null;

            for (let i = 0; i < tables.length; i++) {
                const table = tables[i];
                const dataRows = table.querySelectorAll('tbody tr');
                console.log(`📊 Tabla ${i}: ${dataRows.length} filas encontradas`);

                if (dataRows.length > 0) {
                    tableConDatos = table;
                    console.log(`✅ Usando tabla ${i} con ${dataRows.length} filas`);
                    break;
                }
            }

            if (!tableConDatos) {
                console.warn('⚠️ No se encontró tabla con datos');
                console.log('🔍 Revisando estructura DOM...');

                // ✅ LOG DETALLADO DE LA ESTRUCTURA
                tables.forEach((table, index) => {
                    console.log(`📋 Tabla ${index}:`);
                    console.log(`  - Thead: ${table.querySelectorAll('thead th, thead td').length} headers`);
                    console.log(`  - Tbody: ${table.querySelectorAll('tbody').length} tbody elements`);
                    console.log(`  - Tbody tr: ${table.querySelectorAll('tbody tr').length} filas`);
                    console.log(`  - Contenido tbody:`, table.querySelector('tbody')?.innerHTML?.substring(0, 200));
                });

                // ✅ USAR LA ÚLTIMA TABLA COMO FALLBACK
                tableConDatos = tables[tables.length - 1];
            }

            // ✅ EXTRAER HEADERS (priorizar DOM; si falla, inferir por número de celdas)
            const headerCells = tableConDatos.querySelectorAll('thead th, thead td');
            if (headerCells.length > 0) {
                const extractedHeaders = Array.from(headerCells).map(cell =>
                    this.cleanHeaderText(cell.textContent)
                ).filter(header => header !== '');

                if (extractedHeaders.length > 0) {
                    tableData.headers = extractedHeaders;
                }
            }

            // ✅ INFERIR HEADERS SI EL DOM NO DIO RESULTADO O HAY DESFASE POR OPCIONALES
            if (!tableData.headers || tableData.headers.length === 0) {
                tableData.headers = [...baseHeaders];
            }

            // ✅ EXTRAER FILAS CON LOGS DETALLADOS
            const dataRows = tableConDatos.querySelectorAll('tbody tr');
            console.log(`📊 Procesando ${dataRows.length} filas de RecorridosTableList...`);

            if (dataRows.length === 0) {
                console.log('🔍 No se encontraron filas, revisando estructura tbody...');
                const tbody = tableConDatos.querySelector('tbody');
                if (tbody) {
                    console.log('📋 Contenido tbody:', tbody.innerHTML.substring(0, 500));
                }
            }

            dataRows.forEach((row, index) => {
                const cells = row.querySelectorAll('td');
                console.log(`📊 Fila ${index}: ${cells.length} celdas`);

                if (cells.length === 0) {
                    return;
                }

                // Detectar cuántas columnas reales hay según la fila
                const hasMapIcon = (cell) => cell.querySelector('a .fa-map-marker-alt') || cell.querySelector('button .fa-map-marker-alt');

                // Inferir headers según la cantidad de celdas (opcional Random/Tiempo abierto)
                if (index === 0) {
                    const cellCount = cells.length;
                    if (cellCount >= baseHeaders.length + 2 && shouldShowExtras) {
                        tableData.headers = [...baseHeaders.slice(0, 3), ...optionalHeaders, ...baseHeaders.slice(3)];
                    } else if (cellCount === baseHeaders.length + 1 && shouldShowExtras) {
                        tableData.headers = [...baseHeaders.slice(0, 3), optionalHeaders[0], ...baseHeaders.slice(3)];
                    } else if (cellCount === baseHeaders.length) {
                        tableData.headers = [...baseHeaders];
                    }
                }

                const expected = tableData.headers.length;
                const offset = Math.max(0, cells.length - expected);
                const alignedCells = Array.from(cells).slice(offset, offset + expected);

                if (alignedCells.length < expected) {
                    return; // evitar filas incompletas
                }

                const rowData = alignedCells.map((cell, cellIndex) => {
                    // Última columna es Ubicación
                    const isLocationColumn = cellIndex === expected - 1;
                    if (isLocationColumn && hasMapIcon(cell)) {
                        const text = cell.querySelector('span')?.textContent?.trim() || this.cleanCellText(cell.textContent);
                        return text || 'Ver Ubicación';
                    }

                    // Manejar columnas opcionales con ícono de mapa
                    if (hasMapIcon(cell)) {
                        return 'Ver Ubicación';
                    }

                    return this.cleanCellText(cell.textContent);
                });

                if (rowData.some(value => value !== '')) {
                    tableData.data.push(rowData);
                    console.log(`✅ Fila ${index} agregada:`, rowData.slice(0, Math.min(3, rowData.length)));
                }
            });

            tableData.totalRows = tableData.data.length;

            console.log(`✅ RecorridosTableList extraída: ${tableData.data.length} filas, ${tableData.headers.length} columnas`);

            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo RecorridosTableList:', error);
            return tableData;
        } finally {
            // ✅ RESTAURAR DESPUÉS DE UN delay
            setTimeout(() => {
                if (window.restoreRecorridosTableAfterPrint) {
                    window.restoreRecorridosTableAfterPrint();
                }
            }, 1000);
        }
    }


    async extractRecorridosTableListFull_10062025(container) {
        console.log('🚗 Extrayendo datos completos de RecorridosTableList...');

        const tableData = {
            headers: ['Fecha', 'Evento', 'Velocidad(Km/h)', 'Marcador', 'Dirección', 'Ubicación'],
            data: [],
            title: 'Listado de Recorridos',
            totalRows: 0,
            source: 'RecorridosTableList'
        };

        try {
            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('.card-header h6') ||
                container.querySelector('[data-table-title="true"]') ||
                container.querySelector('.mb-0');

            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ BUSCAR TODAS LAS TABLAS (RecorridosTableList usa estructura dual)
            const tables = container.querySelectorAll('table');
            console.log(`📊 Tablas encontradas en RecorridosTableList: ${tables.length}`);

            if (tables.length === 0) {
                console.warn('⚠️ No se encontraron tablas en RecorridosTableList');
                return tableData;
            }

            let headerTable = null;
            let dataTable = null;

            // ✅ IDENTIFICAR TABLA DE HEADERS Y TABLA DE DATOS
            tables.forEach((table, index) => {
                const bodyRows = table.querySelectorAll('tbody tr');
                const headerCells = table.querySelectorAll('thead th, thead td');

                console.log(`Tabla ${index + 1}: Headers=${headerCells.length}, Filas=${bodyRows.length}`);

                // La tabla con headers válidos pero sin datos es la tabla de headers
                if (headerCells.length > 0 && bodyRows.length === 0) {
                    headerTable = table;
                    console.log(`📋 Tabla de headers identificada: Tabla ${index + 1}`);
                }

                // La tabla con datos es la que tiene filas en tbody
                if (bodyRows.length > 0) {
                    dataTable = table;
                    console.log(`📊 Tabla de datos identificada: Tabla ${index + 1} (${bodyRows.length} filas)`);
                }
            });

            // ✅ EXTRAER HEADERS DE LA TABLA DE HEADERS
            if (headerTable) {
                const headerCells = headerTable.querySelectorAll('thead th, thead td');
                if (headerCells.length > 0) {
                    tableData.headers = Array.from(headerCells).map(cell =>
                        cell.textContent?.trim() || ''
                    ).filter(header => header !== ''); // Filtrar headers vacíos
                    console.log('📋 Headers extraídos de tabla dedicada:', tableData.headers);
                }
            }

            // ✅ EXTRAER DATOS DE LA TABLA DE DATOS
            if (dataTable) {
                const dataRows = dataTable.querySelectorAll('tbody tr');
                console.log(`📊 Procesando ${dataRows.length} filas de datos...`);

                // ✅ PROCESAR CADA FILA
                dataRows.forEach((row, index) => {
                    const cells = row.querySelectorAll('td');

                    if (cells.length > 0) {
                        const rowData = Array.from(cells).map(cell => {
                            // ✅ LIMPIAR CONTENIDO DE LA CELDA
                            let content = cell.textContent?.trim() || '';

                            // ✅ MANEJAR BOTONES Y ELEMENTOS ESPECIALES
                            if (cell.querySelector('button')) {
                                const button = cell.querySelector('button');
                                if (button.querySelector('.fa-map-marker-alt, .text-danger')) {
                                    content = 'Ver Ubicación';
                                }
                            }

                            return content;
                        }).filter(content => content !== ''); // Filtrar celdas vacías

                        if (rowData.length > 0) {
                            tableData.data.push(rowData);
                        }
                    }
                });
            } else {
                console.warn('⚠️ No se encontró tabla de datos en RecorridosTableList');

                // ✅ FALLBACK: Intentar extraer de la primera tabla disponible
                if (tables.length > 0) {
                    const fallbackTable = tables[tables.length - 1]; // Usar la última tabla
                    const allRows = fallbackTable.querySelectorAll('tbody tr');

                    console.log(`🔄 Fallback: Extrayendo de tabla con ${allRows.length} filas`);

                    allRows.forEach(row => {
                        const cells = row.querySelectorAll('td');
                        if (cells.length > 0) {
                            const rowData = Array.from(cells).map(cell => {
                                let content = cell.textContent?.trim() || '';
                                if (cell.querySelector('button .fa-map-marker-alt')) {
                                    content = 'Ver Ubicación';
                                }
                                return content;
                            }).filter(content => content !== '');

                            if (rowData.length > 0) {
                                tableData.data.push(rowData);
                            }
                        }
                    });
                }
            }

            tableData.totalRows = tableData.data.length;

            console.log(`✅ RecorridosTableList extraída: ${tableData.data.length} filas, ${tableData.headers.length} columnas`);
            console.log('📋 Headers finales:', tableData.headers);
            console.log('📊 Primeras 3 filas:', tableData.data.slice(0, 3));

            return tableData;

        } catch (error) {
            console.error('❌ Error extrayendo RecorridosTableList:', error);
            return tableData;
        }
    }



    async extract(container, tableType = 'auto') {
        console.log('🔍 Iniciando extracción de tabla:', { tableType, container });

        if (!container) {
            console.warn('⚠️ No se proporcionó contenedor para extracción');
            return this.getEmptyTableData();
        }

        try {
            // ✅ DETECTAR TIPO DE TABLA
            if (tableType === 'auto') {
                tableType = this.detectTableType(container);
            }

            console.log(`📊 Tipo de tabla detectado/especificado: ${tableType}`);

            // ✅ AGREGAR CASO PARA RECORRIDOS
            switch (tableType) {
                case 'RecorridosTableList': // ✅ NUEVO CASO
                    return await this.extractRecorridosTableListFull(container);

                case 'AlertaTableListTanstack':
                    return await this.extractAlertaTableListTanstackFull(container);

                case 'BitacoraTableListTanstack': // ✅ NUEVO CASO
                    return await this.extractBitacoraTableListTanstack(container);
                case 'ConsumoTableListTanstack': // ✅ NUEVO CASO
                    return await this.extractConsumosTableListTanstack(container);

                case 'html':
                    return this.extractFromHTMLTable(container);

                case 'react-table':
                    return this.extractFromReactTable(container);
                case 'HTMLTable':
                    return await this.extractHTMLTable(container);
                case 'EstadiasTableList':
                    return await this.extractEstadiasTableList(container);
                default:
                    console.log('🔄 Intentando extracción automática...');

                    // ✅ PRIORIZAR RECORRIDOS SI SE DETECTA
                    if (container.querySelector('.card-header') &&
                        container.querySelector('table') &&
                        (container.textContent.includes('Recorrido') ||
                            container.textContent.includes('Evento') ||
                            container.textContent.includes('Marcador'))) {
                        console.log('🚗 Detectado RecorridosTableList, usando extractor específico');
                        return await this.extractRecorridosTableListFull(container);
                    }

                    // ✅ MANTENER LÓGICA EXISTENTE PARA ALERTAS
                    if (container.querySelector('.alert-row[data-cell]')) {
                        console.log('🚨 Detectado AlertaTableListTanstack, usando extractor específico');
                        return await this.extractAlertaTableListTanstackFull(container);
                    }

                    return this.extractFromHTMLTable(container);
            }

        } catch (error) {
            console.error('❌ Error en extracción de tabla:', error);
            return this.getEmptyTableData();
        }
    }


    /**
     * ✅ DETECTAR TIPO DE TABLA - VERSIÓN QUE FUNCIONABA + BITÁCORAS
     */
    detectTableType(container) {
        const text = container.textContent || '';

        // ✅ PRIORIDAD: DETECTAR ESTADÍAS POR ID O TEXTO ANTES DE HEURÍSTICAS DE RECORRIDOS
        if (container.id === 'tabla-estadias' || (container.closest && container.closest('#tabla-estadias'))) {
            console.log('📍 Detectada EstadiasTableList por ID #tabla-estadias');
            return 'EstadiasTableList';
        }

        if (text.toLowerCase().includes('estadia')) {
            console.log('📍 Detectada EstadiasTableList por texto');
            return 'EstadiasTableList';
        }

        if (container.querySelector('.card-header') &&
            container.querySelector('table') &&
            (text.includes('Listado Recorridos') ||
                text.includes('Reporte de Recorridos') ||
                (text.includes('Fecha') && text.includes('Evento') && text.includes('Velocidad')) ||
                (text.includes('Marcador') && text.includes('Dirección')))) {

            console.log('🚗 Detectada RecorridosTableList por contenido específico');
            return 'RecorridosTableList';
        }

        // ✅ DETECTAR POR ID ESPECÍFICO
        if (container.id === 'tabla-recorridos' ||
            container.closest('#tabla-recorridos')) {
            console.log('🚗 Detectada RecorridosTableList por ID #tabla-recorridos');
            return 'RecorridosTableList';
        }

        // ✅ MANTENER: DETECTAR ALERTAS TANSTACK (YA FUNCIONABA)
        if (container.querySelector('.alert-row[data-cell]')) {
            console.log('🚨 Detectada tabla AlertaTableListTanstack (CON data-cell)');
            return 'AlertaTableListTanstack';
        }

        if (container.querySelector('[data-table-headers="true"]')) {
            console.log('🚨 Detectada tabla AlertaTableListTanstack (headers)');
            return 'AlertaTableListTanstack';
        }

        // ✅ NUEVO: DETECTAR BITÁCORAS TANSTACK (SIN data-cell)
        if (container.querySelector('.alert-row') &&
            container.querySelector('.table-header') &&
            container.querySelector('.list-striped') &&
            !container.querySelector('.alert-row[data-cell]') && // SIN data-cell
            (text.includes('Bitácora') || text.includes('Observaciones'))) {
            console.log('📝 Detectada tabla BitacoraTableListTanstack (SIN data-cell)');
            return 'BitacoraTableListTanstack';
        }

        if (container.querySelector('.alert-row') &&
            container.querySelector('.table-header') &&
            container.querySelector('.list-striped') &&
            ((text.includes('SMS') || text.includes('Correo') || text.includes('Whatsapp') || text.includes('Voz')))) {
            console.log('📝 Detectada tabla BitacoraTableListTanstack (SIN data-cell)');
            return 'ConsumoTableListTanstack';
        }

        // ✅ MANTENER: DETECTAR RECORRIDOS
        if (container.querySelector('.recorrido-item') ||
            text.includes('Recorrido') || text.includes('recorrido')) {
            return 'RecorridosTableList';
        }

        // ✅ MANTENER: DETECTAR PARADAS NO AUTORIZADAS
        if (container.querySelector('table') &&
            (text.includes('Paradas') || text.includes('Detenido'))) {
            return 'ParadasNoAutorizadas';
        }

        // ✅ MANTENER: DETECTAR TABLA HTML GENÉRICA
        if (container.querySelector('table')) {
            return 'HTMLTable';
        }

        // ✅ FALLBACK
        console.warn('⚠️ Tipo de tabla no reconocido, usando genérico');
        return 'ReactTable';
    }




    detectTableType_resapdlo(container) {
        const text = container.textContent || '';

        try {
            // ✅ DETECTAR RECORRIDOS PRIMERO (más específico)
            if (container.querySelector('.card-header') &&
                container.querySelector('table') &&
                (container.textContent.includes('Recorrido') ||
                    container.textContent.includes('Listado') ||
                    container.textContent.includes('Evento') ||
                    container.textContent.includes('Marcador') ||
                    container.textContent.includes('Velocidad'))) {

                // ✅ VERIFICAR ESTRUCTURA DUAL DE TABLAS (específico de RecorridosTableList)
                const tables = container.querySelectorAll('table');
                if (tables.length >= 2) {
                    console.log('🚗 Detectada estructura dual de RecorridosTableList');
                    return 'RecorridosTableList';
                }

                // ✅ VERIFICAR SI HAY DATOS DE RECORRIDOS
                const hasRecorridosData = container.querySelector('tbody tr td') &&
                    (container.textContent.includes('Apagado') ||
                        container.textContent.includes('Movimiento') ||
                        container.textContent.includes('Detenido'));

                if (hasRecorridosData) {
                    return 'RecorridosTableList';
                }
            }

            // ✅ MANTENER DETECCIÓN EXISTENTE PARA ALERTAS
            if (container.querySelector('.alert-row[data-cell]')) {
                return 'AlertaTableListTanstack';
            }

            if (container.querySelector('.alert-row') &&
                container.querySelector('.table-header') &&
                container.querySelector('.list-striped') &&
                !container.querySelector('.alert-row[data-cell]') && // Sin data-cell
                (text.includes('Bitácora') || text.includes('Observaciones') || text.includes('Nombre Usuario'))) {
                console.log('📝 Detectada tabla BitacoraTableListTanstack');
                return 'BitacoraTableListTanstack';
            }


            if (container.querySelector('[data-table-headers="true"]')) {
                return 'AlertaTableListTanstack';
            }

            if (container.querySelector('table')) {
                return 'html';
            }

            if (container.querySelector('[role="table"]')) {
                return 'react-table';
            }

            return 'unknown';

        } catch (error) {
            console.error('❌ Error detectando tipo de tabla:', error);
            return 'unknown';
        }
    }

    /**
     * ✅ EXTRACTOR PARA TABLAS HTML GENÉRICAS
     */
    async extractFromHTMLTable(container) {
        console.log('🔧 Extrayendo tabla HTML genérica...');

        const tableData = {
            headers: [],
            data: [],
            title: 'Datos de Tabla HTML',
            totalRows: 0,
            source: 'HTMLTable'
        };

        try {
            const table = container.querySelector('table');
            if (!table) {
                console.warn('⚠️ No se encontró tabla HTML');
                return tableData;
            }

            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('.card-header h6, h5, h4, h3') ||
                container.querySelector('[data-table-title="true"]');
            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ EXTRAER HEADERS
            const headerCells = table.querySelectorAll('thead th, thead td');
            if (headerCells.length > 0) {
                tableData.headers = Array.from(headerCells).map(cell =>
                    this.cleanHeaderText(cell.textContent)
                ).filter(header => header !== '');
            }

            // ✅ EXTRAER FILAS
            const dataRows = table.querySelectorAll('tbody tr');
            dataRows.forEach(row => {
                const cells = row.querySelectorAll('td');
                if (cells.length > 0) {
                    const rowData = Array.from(cells).map(cell => {
                        let content = this.cleanCellText(cell.textContent);

                        // ✅ MANEJAR BOTONES ESPECIALES
                        if (cell.querySelector('button .fa-map-marker-alt')) {
                            content = 'Ver Ubicación';
                        }

                        return content;
                    }).filter(content => content !== '');

                    if (rowData.length > 0) {
                        tableData.data.push(rowData);
                    }
                }
            });

            tableData.totalRows = tableData.data.length;

            console.log(`✅ Tabla HTML extraída: ${tableData.data.length} filas`);
            return tableData;

        } catch (error) {
            console.error('❌ Error en extractFromHTMLTable:', error);
            return tableData;
        }
    }

    /**
     * ✅ EXTRACTOR PARA REACT TABLE GENÉRICO
     */
    async extractFromReactTable(container) {
        console.log('🔧 Extrayendo React Table genérico...');

        const tableData = {
            headers: [],
            data: [],
            title: 'Datos de React Table',
            totalRows: 0,
            source: 'ReactTable'
        };

        try {
            // ✅ EXTRAER TÍTULO
            const titleElement = container.querySelector('.card-header h6, h5, h4, h3') ||
                container.querySelector('[data-table-title="true"]');
            if (titleElement) {
                tableData.title = titleElement.textContent.trim();
            }

            // ✅ BUSCAR CUALQUIER ESTRUCTURA TABULAR
            const potentialRows = container.querySelectorAll(
                '[role="row"], .table-row, .data-row, [class*="row"], tbody tr'
            );

            if (potentialRows.length === 0) {
                console.warn('⚠️ No se encontraron filas en React Table');
                return tableData;
            }

            console.log(`📊 Procesando ${potentialRows.length} filas potenciales...`);

            // ✅ EXTRAER DATOS DE FILAS
            potentialRows.forEach((row, index) => {
                const cells = row.querySelectorAll(
                    '[role="cell"], .table-cell, .data-cell, td, [class*="cell"]'
                );

                if (cells.length > 0) {
                    const rowData = Array.from(cells).map(cell =>
                        this.cleanCellText(cell.textContent)
                    ).filter(content => content !== '');

                    if (rowData.length > 0) {
                        tableData.data.push(rowData);
                    }
                }
            });

            // ✅ GENERAR HEADERS AUTOMÁTICAMENTE
            if (tableData.data.length > 0) {
                const maxColumns = Math.max(...tableData.data.map(row => row.length));
                tableData.headers = Array.from({ length: maxColumns }, (_, i) => `Columna ${i + 1}`);
            }

            tableData.totalRows = tableData.data.length;

            console.log(`✅ React Table extraído: ${tableData.data.length} filas`);
            return tableData;

        } catch (error) {
            console.error('❌ Error en extractFromReactTable:', error);
            return tableData;
        }
    }

    /**
     * ✅ MÉTODO PARA DATOS VACÍOS
     */
    getEmptyTableData() {
        return {
            headers: [],
            data: [],
            title: 'Sin Datos',
            totalRows: 0,
            source: 'empty'
        };
    }

    /**
     * ✅ ALIAS PARA COMPATIBILIDAD
     */
    async extractHTMLTable(container) {
        return await this.extractFromHTMLTable(container);
    }

    async extractParadasNoAutorizadas(container) {
        console.log('🛑 Extrayendo Paradas No Autorizadas...');
        return await this.extractFromHTMLTable(container);
    }





}

export default TableDataExtractor;